import Alert from '@mui/material/Alert';
import { SpaceType } from '@onesource/schemas';
import { useGetSpace, useGetSubSpaces } from '@onesource/services';
import { createFileRoute } from '@tanstack/react-router';
import { SingleLessonView } from 'src/features/community-hub/components/single-lesson-view';
import { SpaceHomePage } from 'src/features/community-hub/components/space-home-page';
import { SpaceHomePageSkeleton } from 'src/features/community-hub/components/space-home-page-skeleton';
import { useContentRoute } from 'src/features/community-hub/hooks/use-content-route';
import { usePreviewMode } from 'src/features/community-hub/hooks/use-preview-mode';

/** Sub-space types that get a card on the program home (in admin position order). */
const HOME_CARD_TYPES = new Set<SpaceType>([SpaceType.CONTENT, SpaceType.ANALYTICS, SpaceType.REVIEW]);

/**
 * Program home. Path: /programs/{programSlug}
 * Container programs list their modules (and any "My Analytics" sub-space);
 * single-lesson programs render their one lesson directly (they have no modules).
 */
export const Route = createFileRoute('/_protected/programs/$programSlug/')({
  component: ProgramHomeRoute,
});

function ProgramHomeRoute() {
  const { programId } = useContentRoute();
  const { isPreview } = usePreviewMode();
  const { data: space, isLoading: isLoadingSpace, error: spaceError } = useGetSpace(programId);
  const { data: subSpaces, isLoading: isLoadingSubSpaces } = useGetSubSpaces(programId, { preview: isPreview });

  if (isLoadingSpace) {
    return <SpaceHomePageSkeleton />;
  }

  if (spaceError) {
    return <Alert severity="error">Failed to load program: {spaceError.message}</Alert>;
  }

  if (!space) {
    return <Alert severity="error">Program not found</Alert>;
  }

  if (space.spaceType === SpaceType.SINGLE_LESSON) {
    return <SingleLessonView />;
  }

  if (isLoadingSubSpaces) {
    return <SpaceHomePageSkeleton />;
  }

  const modules = subSpaces?.filter(s => HOME_CARD_TYPES.has(s.spaceType)) || [];
  return <SpaceHomePage space={space} modules={modules} />;
}
